/**
 * Preview pane for the plugin builder.
 * Renders the built plugin in a sandboxed iframe.
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Code2, Eye, AlertCircle } from 'lucide-react';
import { CodeViewerPane } from './CodeViewerPane';
import type { VirtualFileSystem } from '../types';

interface PluginPreviewPaneProps {
  previewHtml: string | null;
  files: VirtualFileSystem | null;
  isBuildingPreview: boolean;
  buildError: string | null;
  onBuildPreview: () => Promise<string | null>;
  className?: string;
}

type Tab = 'preview' | 'code';

export function PluginPreviewPane({
  previewHtml,
  files,
  isBuildingPreview,
  buildError,
  onBuildPreview,
  className,
}: PluginPreviewPaneProps) {
  const [activeTab, setActiveTab] = useState<Tab>('preview');

  const fileCount = files ? Object.keys(files).length : 0;

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Tab bar */}
      <div className="flex items-center border-b bg-muted/20 px-3 py-1.5 gap-1 flex-shrink-0">
        <button
          onClick={() => setActiveTab('preview')}
          className={cn(
            'flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-md transition-colors',
            activeTab === 'preview'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Eye className="h-3.5 w-3.5" />
          Preview
        </button>
        <button
          onClick={() => setActiveTab('code')}
          className={cn(
            'flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-md transition-colors',
            activeTab === 'code'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Code2 className="h-3.5 w-3.5" />
          Code
          {fileCount > 0 && (
            <span className="text-[10px] px-1 rounded-full bg-muted text-muted-foreground">
              {fileCount}
            </span>
          )}
        </button>

        {/* Build button */}
        {activeTab === 'preview' && (
          <div className="ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBuildPreview}
              disabled={isBuildingPreview || !files || fileCount === 0}
              className="h-7 text-xs"
            >
              {isBuildingPreview ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
              )}
              Build
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {activeTab === 'preview' ? (
          <PreviewContent
            previewHtml={previewHtml}
            isBuildingPreview={isBuildingPreview}
            buildError={buildError}
            fileCount={fileCount}
          />
        ) : (
          <CodeViewerPane files={files} className="h-full" />
        )}
      </div>
    </div>
  );
}

function PreviewContent({
  previewHtml,
  isBuildingPreview,
  buildError,
  fileCount,
}: {
  previewHtml: string | null;
  isBuildingPreview: boolean;
  buildError: string | null;
  fileCount: number;
}) {
  if (isBuildingPreview) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="text-sm">Building preview...</span>
      </div>
    );
  }

  if (buildError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <div>
          <h4 className="text-sm font-semibold text-destructive mb-1">Build Error</h4>
          <pre className="text-xs text-muted-foreground whitespace-pre-wrap max-w-md">
            {buildError}
          </pre>
        </div>
      </div>
    );
  }

  if (!previewHtml) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <Eye className="h-8 w-8 opacity-30" />
        <span className="text-sm">
          {fileCount > 0 ? 'Click "Build" to preview your plugin' : 'No files to preview yet'}
        </span>
      </div>
    );
  }

  return (
    <iframe
      srcDoc={previewHtml}
      className="w-full h-full border-0"
      sandbox="allow-scripts allow-same-origin"
      title="Plugin Preview"
    />
  );
}
